import { Button, Dialog, DialogActions, DialogTitle, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium, GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-premium';
import { FC, memo, useContext, useEffect, useState } from 'react';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import LicenseContext from 'src/contexts/LicenseContext';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { TruckEntity } from 'src/entities/truckEntity';
import { useTrucksRequest as usePaginationRequest } from 'src/hooks/useTrucks.request';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { Columns } from '../V2TrucksPresenter/Columns';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (maybeTruck: TruckEntity | null) => void;
  selectedCompanyId: number;
  currentTruckId?: number;
};

const SelectTruckDialog: FC<Props> = memo((
  {
    open,
    onClose,
    onSelect,
    currentTruckId,
    selectedCompanyId,
  }
) => {
  const licenseContext = useContext(LicenseContext);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(30);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(
    page,
    sortCondition,
    searchCondition,
    pageSize,
  );

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }

    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
      stringUtil.toSnakeCase(item.field),
      item.operator,
      item.value as string | string[] | undefined,
      filterModel.logicOperator
    ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  const dataGridColumns: GridColDef[] = Columns(licenseContext?.config?.unit).filter((col) => {
    if (licenseContext?.config?.unit_used_for_calculation === '重量と体積') {
      return true;
    }

    return col.field !== 'loading_platform_volume_m3';
  });

  const onSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    if (rowSelectionModel.type !== 'include') return;
    setSelectedIds(Array.from(rowSelectionModel.ids).map((id) => Number(id)));
  };

  const maybeSelectedTruck = requestData?.data.find((e) => selectedIds.includes(e.id));

  const onColumnWidthChange = (params: GridColumnResizeParams) => {
    const columns = dataGridColumns.map((col) => {
      let newDefinition = { field: col.field, width: col.width };
      if (col.field === params.colDef.field) {
        newDefinition = { ...newDefinition, width: params.colDef.width };
      }
      return newDefinition;
    });
    localStorage.setItem('truck-columns-width', JSON.stringify(columns));
  };

  const onSubmit = () => {
    onSelect(maybeSelectedTruck);
    onClose();
  };

  useEffect(() => {
    const maybeCompanyPredicateEntity: RansackPredicateEntity | null = selectedCompanyId ? {
      column: 'company_id',
      predicate: 'eq',
      value: `${selectedCompanyId}`,
      link: 'and'
    } : null;

    const modifiedAndConditions: RansackPredicateEntity[] = maybeCompanyPredicateEntity
      ? [
        maybeCompanyPredicateEntity,
        ...andConditions
      ] : andConditions;

    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', modifiedAndConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions, selectedCompanyId]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData?.totalCount || 0);
  }, [requestData]);

  useEffect(() => {
    if (currentTruckId) {
      setSelectedIds([currentTruckId]);
      return;
    }

    setSelectedIds([]);
  }, [currentTruckId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        mt: 8
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          トラックを選択してください
        </Typography>
      </DialogTitle>
      <DataGridPremium
        rows={requestData?.data || []}
        columns={dataGridColumns}
        pagination
        disableRowGrouping
        initialState={{
          columns: {
            columnVisibilityModel: {
              garage_address: false,
            },
          },
        }}
        slots={{
          loadingOverlay: CustomLoadingOverlay,
        }}
        localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
        loading={requestIsLoading}
        onColumnWidthChange={(params) => onColumnWidthChange(params)}
        onRowSelectionModelChange={onSelectionModelChange}
        rowSelectionModel={{ ids: new Set(selectedIds), type: 'include' }}
        sortingMode="server"
        paginationMode="server"
        filterMode="server"
        rowCount={rowCount}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
          setSelectedIds([]);
        }}
        onSortModelChange={handleSortModelChange}
        onFilterModelChange={onFilterChange}
        sx={{
          mb: 2,
          mx: 2,
        }}
        disableAggregation
        disablePivoting
      />
      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
        >
          キャンセル
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={!maybeSelectedTruck}
        >
          {
            maybeSelectedTruck
              ? `${maybeSelectedTruck.license_plate_value} を設定する`
              : 'トラックを選択してください'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default SelectTruckDialog;
